import { useEffect, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash, CircleNotch, Briefcase, TrendUp, TrendDown, Wallet, Warning, Calculator, Sparkle, ClipboardText, FloppyDisk, Receipt } from "@phosphor-icons/react";
import { RowanTerm } from "@/components/RowanTerm";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const CAT_ORDER = ["Materials", "Labor", "Subcontractor", "Equipment", "Permits", "Other"];

function Row({ label, value, bold, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: bold ? "var(--bk-text)" : "var(--bk-text-2)", fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span className="tabular-nums" style={{ color: accent ? "var(--bk-accent)" : "var(--bk-text)", fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  );
}
const STATUSES = ["Quoted", "In Progress", "Done"];
const STATUS_STYLE = {
  Quoted: { bg: "var(--bk-page)", color: "var(--bk-text-2)" },
  "In Progress": { bg: "#EAF0F6", color: "#3A5A7A" },
  Done: { bg: "var(--bk-success-bg)", color: "#2F6B4F" },
};

export function Jobs({ businessId }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", address: "", status: "Quoted", budget: "", start_date: "", end_date: "", notes: "" });

  const [showEst, setShowEst] = useState(false);
  const [estForm, setEstForm] = useState({ title: "", trade: "", description: "", size: "", location: "", target_margin_pct: "" });
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [savingEst, setSavingEst] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invForm, setInvForm] = useState({ client_name: "", client_email: "", due_date: "" });
  const [creatingInv, setCreatingInv] = useState(false);

  const load = () => api.get(`/businesses/${businessId}/jobs`).then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const runEstimate = async () => {
    if (!estForm.title.trim()) return toast.error("Add a job title");
    if (!estForm.description.trim()) return toast.error("Describe the job scope so I can estimate accurately");
    setEstimating(true);
    setEstimate(null);
    try {
      const payload = { ...estForm, target_margin_pct: estForm.target_margin_pct === "" ? null : Number(estForm.target_margin_pct) };
      const { data } = await api.post(`/businesses/${businessId}/jobs/estimate`, payload);
      setEstimate(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't build the estimate. Please try again.");
    } finally { setEstimating(false); }
  };

  const saveEstimateAsJob = async () => {
    if (!estimate) return;
    setSavingEst(true);
    try {
      const notes = `AI estimate — recommended quote ${money0(estimate.recommended_price)} (cost ${money0(estimate.cost_total)}, ${estimate.profit_margin_pct}% margin).\n${estimate.summary}`.slice(0, 1000);
      await api.post(`/businesses/${businessId}/jobs`, {
        name: estimate.title, status: "Quoted", budget: estimate.cost_total, notes,
      });
      toast.success("Saved as a job — budget set to the estimated cost");
      setShowEst(false); setEstimate(null);
      setEstForm({ title: "", trade: "", description: "", size: "", location: "", target_margin_pct: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the job.");
    } finally { setSavingEst(false); }
  };

  const convertToInvoice = async () => {
    if (!estimate) return;
    if (!invForm.client_name.trim()) return toast.error("Add the client name");
    setCreatingInv(true);
    try {
      const scopeNote = [
        estimate.summary,
        estimate.assumptions?.length ? "Assumptions: " + estimate.assumptions.join("; ") : "",
      ].filter(Boolean).join("\n").slice(0, 1000);
      const { data } = await api.post(`/businesses/${businessId}/invoices`, {
        client_name: invForm.client_name.trim(),
        client_email: invForm.client_email.trim(),
        due_date: invForm.due_date || "",
        items: [{ description: `${estimate.title} — project (see scope)`, quantity: 1, rate: estimate.recommended_price }],
        notes: scopeNote,
      });
      toast.success(`Invoice ${data.number} created — send it to get paid`, {
        action: { label: "Open invoices", onClick: () => navigate(`/business/${businessId}?tab=invoices`) },
      });
      setShowEst(false); setEstimate(null); setShowInvoice(false);
      setInvForm({ client_name: "", client_email: "", due_date: "" });
      setEstForm({ title: "", trade: "", description: "", size: "", location: "", target_margin_pct: "" });
      navigate(`/business/${businessId}?tab=invoices`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create the invoice.");
    } finally { setCreatingInv(false); }
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Add a job name");
    setSaving(true);
    try {
      await api.post(`/businesses/${businessId}/jobs`, { ...form, budget: Number(form.budget) || 0 });
      toast.success("Job created");
      setForm({ name: "", client: "", address: "", status: "Quoted", budget: "", start_date: "", end_date: "", notes: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create job.");
    } finally { setSaving(false); }
  };

  const setStatus = async (job, status) => {
    try {
      const { data } = await api.patch(`/jobs/${job.id}`, { status });
      setJobs((xs) => xs.map((x) => (x.id === job.id ? data : x)));
    } catch { toast.error("Couldn't update job."); }
  };

  const del = async (id) => {
    try { await api.delete(`/jobs/${id}`); setJobs((x) => x.filter((j) => j.id !== id)); } catch {}
  };

  return (
    <div className="space-y-6" data-testid="jobs-panel">
      <div className="bk-card p-6 flex items-center justify-between">
        <div className="flex items-start gap-3">
          <Briefcase size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}><RowanTerm term="job_costing">Jobs & profitability</RowanTerm></h3>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Track each project's profit. Tag income, expenses, and invoices to a job to see live margins.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => { setShowEst((s) => !s); setShowForm(false); }} data-testid="ai-estimate-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border transition-colors hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
            <Calculator size={16} weight="bold" /> AI Estimate
          </button>
          <button onClick={() => { setShowForm((s) => !s); setShowEst(false); }} data-testid="new-job-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold">
            <Plus size={16} weight="bold" /> New job
          </button>
        </div>
      </div>

      {showEst && (
        <div className="bk-card p-6 space-y-4" data-testid="estimate-form">
          <div className="flex items-start gap-3">
            <Sparkle size={22} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>AI estimate calculator</h4>
              <p className="text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>Describe the job and specs — AI builds an itemized estimate with materials, labor, subs, overhead & a recommended quote.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Job title (e.g. Kitchen remodel)" value={estForm.title} onChange={(e) => setEstForm((f) => ({ ...f, title: e.target.value }))} data-testid="est-title" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input placeholder="Trade / type (e.g. Roofing, Remodel)" value={estForm.trade} onChange={(e) => setEstForm((f) => ({ ...f, trade: e.target.value }))} data-testid="est-trade" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input placeholder="Size / scale (e.g. 1,200 sq ft)" value={estForm.size} onChange={(e) => setEstForm((f) => ({ ...f, size: e.target.value }))} data-testid="est-size" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input placeholder="Location (city, state)" value={estForm.location} onChange={(e) => setEstForm((f) => ({ ...f, location: e.target.value }))} data-testid="est-location" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          </div>
          <textarea placeholder="Scope & specifications — materials, finishes, quantities, site conditions, anything that affects price…" value={estForm.description} onChange={(e) => setEstForm((f) => ({ ...f, description: e.target.value }))} data-testid="est-description" rows={4} className="w-full rounded-md border px-3 py-2 text-sm outline-none resize-y" style={{ borderColor: "var(--bk-border)" }} />
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm" style={{ color: "var(--bk-text-2)" }}>Target profit margin</label>
              <input type="number" placeholder="15" value={estForm.target_margin_pct} onChange={(e) => setEstForm((f) => ({ ...f, target_margin_pct: e.target.value }))} data-testid="est-margin" className="w-20 rounded-md border px-2 py-1.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              <span className="text-sm" style={{ color: "var(--bk-text-2)" }}>%</span>
            </div>
            <button onClick={runEstimate} disabled={estimating} data-testid="run-estimate-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold disabled:opacity-70 ml-auto">
              {estimating ? <><CircleNotch size={16} className="animate-spin" /> Estimating…</> : <><Calculator size={16} weight="bold" /> Build estimate</>}
            </button>
          </div>

          {estimate && (
            <div className="mt-2 rounded-lg border p-5 space-y-4" data-testid="estimate-result" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{estimate.title}</p>
                  <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{estimate.summary}</p>
                  {estimate.timeline && <p className="text-xs mt-1.5" style={{ color: "var(--bk-text-2)" }}>⏱ {estimate.timeline}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs uppercase tracking-wide" style={{ color: "var(--bk-text-2)" }}>Recommended quote</p>
                  <p className="font-display text-3xl font-bold" style={{ color: "var(--bk-accent)" }} data-testid="est-recommended-price">{money0(estimate.recommended_price)}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--bk-success)" }}>{money0(estimate.profit_amount)} profit · {estimate.profit_margin_pct}% margin</p>
                </div>
              </div>

              {CAT_ORDER.filter((c) => estimate.line_items.some((i) => i.category === c)).map((cat) => (
                <div key={cat}>
                  <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wide mb-1" style={{ color: "var(--bk-text-2)" }}>
                    <span>{cat}</span><span>{money0(estimate.by_category[cat])}</span>
                  </div>
                  <div className="space-y-1">
                    {estimate.line_items.filter((i) => i.category === cat).map((it, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm gap-3 py-0.5" data-testid="est-line-item">
                        <span className="min-w-0 truncate" style={{ color: "var(--bk-text)" }}>{it.description}</span>
                        <span className="shrink-0 tabular-nums" style={{ color: "var(--bk-text-2)" }}>{it.quantity} {it.unit} × {money0(it.unit_cost)} = <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{money0(it.amount)}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="border-t pt-3 space-y-1 text-sm" style={{ borderColor: "var(--bk-border)" }}>
                <Row label="Direct cost subtotal" value={money0(estimate.subtotal)} />
                <Row label={`Overhead (${estimate.overhead_pct}%)`} value={money0(estimate.overhead_amount)} />
                <Row label={`Contingency (${estimate.contingency_pct}%)`} value={money0(estimate.contingency_amount)} />
                <Row label="Total cost to you" value={money0(estimate.cost_total)} bold />
                <Row label={`Profit (${estimate.profit_margin_pct}% margin)`} value={money0(estimate.profit_amount)} accent />
                <Row label="Recommended quote" value={money0(estimate.recommended_price)} bold accent />
              </div>

              {estimate.assumptions?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: "var(--bk-text-2)" }}>Assumptions</p>
                  <ul className="text-xs space-y-0.5 list-disc pl-4" style={{ color: "var(--bk-text-2)" }}>
                    {estimate.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
              <p className="text-xs italic" style={{ color: "var(--bk-text-2)" }}>{estimate.disclaimer}</p>

              <div className="flex flex-wrap gap-2">
                <button onClick={saveEstimateAsJob} disabled={savingEst} data-testid="save-estimate-job-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-70">
                  {savingEst ? <><CircleNotch size={16} className="animate-spin" /> Saving…</> : <><FloppyDisk size={16} weight="bold" /> Save as job</>}
                </button>
                <button onClick={() => setShowInvoice((s) => !s)} data-testid="convert-invoice-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border transition-colors hover:bg-white" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-accent)" }}>
                  <Receipt size={16} weight="bold" /> Convert to invoice
                </button>
                <button onClick={() => { navigator.clipboard?.writeText(`${estimate.title}\nRecommended quote: ${money0(estimate.recommended_price)}\n\n` + estimate.line_items.map((i) => `${i.category}: ${i.description} — ${i.quantity} ${i.unit} × ${money0(i.unit_cost)} = ${money0(i.amount)}`).join("\n")); toast.success("Estimate copied to clipboard"); }} data-testid="copy-estimate-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border transition-colors hover:bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>
                  <ClipboardText size={16} weight="bold" /> Copy
                </button>
              </div>

              {showInvoice && (
                <div className="rounded-lg border p-4 space-y-3 bg-white" data-testid="invoice-form" style={{ borderColor: "var(--bk-border)" }}>
                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Bill the client {money0(estimate.recommended_price)} for “{estimate.title}”</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <input placeholder="Client name" value={invForm.client_name} onChange={(e) => setInvForm((f) => ({ ...f, client_name: e.target.value }))} data-testid="inv-client-name" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                    <input placeholder="Client email (optional)" value={invForm.client_email} onChange={(e) => setInvForm((f) => ({ ...f, client_email: e.target.value }))} data-testid="inv-client-email" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                    <DateInput value={invForm.due_date} onChange={(e) => setInvForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="inv-due-date" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                  </div>
                  <div className="flex justify-end">
                    <button onClick={convertToInvoice} disabled={creatingInv} data-testid="create-invoice-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold disabled:opacity-70">
                      {creatingInv ? <><CircleNotch size={16} className="animate-spin" /> Creating…</> : <><Receipt size={16} weight="bold" /> Create invoice & get paid</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="bk-card p-6 space-y-3" data-testid="job-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Job name (e.g. Kitchen remodel)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="job-name" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input placeholder="Client (optional)" value={form.client} onChange={(e) => setForm((f) => ({ ...f, client: e.target.value }))} data-testid="job-client" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input placeholder="Site address (optional)" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="job-address" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input type="number" placeholder="Budget / estimate ($)" value={form.budget} onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))} data-testid="job-budget" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="job-status" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex justify-end">
            <button onClick={create} disabled={saving} data-testid="save-job-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold disabled:opacity-70">
              {saving ? <><CircleNotch size={16} className="animate-spin" /> Saving…</> : <>Create job</>}
            </button>
          </div>
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="bk-card p-10 text-center">
          <Briefcase size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
          <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>No jobs yet</p>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Create a job, then tag transactions and invoices to it to track profit.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {jobs.map((job) => {
            const ss = STATUS_STYLE[job.status] || STATUS_STYLE.Quoted;
            const profitColor = job.profit >= 0 ? "var(--bk-success)" : "#B23B1F";
            return (
              <div key={job.id} data-testid={`job-${job.id}`} className="bk-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-bold truncate" style={{ color: "var(--bk-text)" }}>{job.name}</p>
                    <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>{job.client || "—"}{job.address ? ` · ${job.address}` : ""}</p>
                  </div>
                  <select value={job.status} onChange={(e) => setStatus(job, e.target.value)} data-testid={`job-status-${job.id}`} className="text-xs font-bold px-2 py-1 rounded-full bg-white border outline-none shrink-0" style={{ borderColor: "var(--bk-border)", background: ss.bg, color: ss.color }}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div className="rounded-md p-2.5" style={{ background: "var(--bk-page)" }}>
                    <div className="flex items-center gap-1 text-xs" style={{ color: "var(--bk-text-2)" }}><TrendUp size={13} /> Revenue</div>
                    <p className="font-semibold text-sm mt-0.5" style={{ color: "var(--bk-success)" }} data-testid={`job-revenue-${job.id}`}>{money(job.revenue)}</p>
                  </div>
                  <div className="rounded-md p-2.5" style={{ background: "var(--bk-page)" }}>
                    <div className="flex items-center gap-1 text-xs" style={{ color: "var(--bk-text-2)" }}><TrendDown size={13} /> Costs</div>
                    <p className="font-semibold text-sm mt-0.5" style={{ color: "#D96C4A" }} data-testid={`job-costs-${job.id}`}>{money(job.costs)}</p>
                    {job.rental_costs > 0 && (
                      <p className="text-[11px] mt-0.5 leading-tight" style={{ color: "var(--bk-text-2)" }} data-testid={`job-rental-costs-${job.id}`}>incl. {money(job.rental_costs)} rentals</p>
                    )}
                  </div>
                  <div className="rounded-md p-2.5" style={{ background: "var(--bk-success-bg)" }}>
                    <div className="flex items-center gap-1 text-xs" style={{ color: "var(--bk-text-2)" }}><Wallet size={13} /> Profit</div>
                    <p className="font-bold text-sm mt-0.5" style={{ color: profitColor }} data-testid={`job-profit-${job.id}`}>{money(job.profit)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>
                    {job.revenue > 0 ? `${job.margin}% margin` : "No tagged revenue yet"}{job.budget > 0 ? ` · budget ${money(job.budget)}` : ""}
                  </span>
                  <button onClick={() => del(job.id)} data-testid={`job-delete-${job.id}`} className="p-1.5 rounded hover:bg-black/5" style={{ color: "#B23B1F" }} aria-label="Delete"><Trash size={15} weight="bold" /></button>
                </div>
                {job.budget > 0 && (
                  <div className="mt-3" data-testid={`job-budget-${job.id}`}>
                    <div className="flex justify-between text-xs mb-1">
                      <span style={{ color: "var(--bk-text-2)" }}>Budget used</span>
                      <span className="font-semibold" style={{ color: job.budget_alert === "over" ? "#B23B1F" : job.budget_alert === "near" ? "#B5761F" : "var(--bk-text-2)" }}>{job.budget_used_pct}%</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--bk-page)" }}>
                      <div className="h-2 rounded-full" style={{ width: `${Math.min(100, job.budget_used_pct)}%`, background: job.budget_alert === "over" ? "#B23B1F" : job.budget_alert === "near" ? "#D9A441" : "var(--bk-success)", transition: "width 0.4s ease" }} />
                    </div>
                    {job.budget_alert && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs font-semibold" style={{ color: job.budget_alert === "over" ? "#B23B1F" : "#B5761F" }} data-testid={`job-budget-alert-${job.id}`}>
                        <Warning size={14} weight="fill" />
                        {job.budget_alert === "over"
                          ? `Over budget by ${money(job.costs - job.budget)} — this is eating your profit.`
                          : `Nearing budget (${job.budget_used_pct}% used) — keep an eye on costs.`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
